import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  LogOut, RefreshCw, FileText, Clock, CheckCircle, 
  XCircle, Phone, Calendar, User, Car, Wrench, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchQuotes();
  }, [token, navigate]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/quotes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setQuotes(data.quotes);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (quoteId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Status atualizado!');
        fetchQuotes();
      } else {
        toast.error('Erro ao atualizar status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Tem certeza que deseja excluir este orçamento?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/quotes/${quoteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Orçamento excluído!');
        fetchQuotes();
      } else {
        toast.error('Erro ao excluir orçamento');
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Erro ao excluir orçamento');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logout realizado');
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      contacted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Phone },
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    const labels = {
      pending: 'Pendente',
      contacted: 'Contactado',
      completed: 'Concluído',
      cancelled: 'Cancelado'
    };

    return (
      <Badge className={`${style.bg} ${style.text} flex items-center gap-1`}>
        <Icon size={14} />
        {labels[status] || status}
      </Badge>
    );
  };

  const filteredQuotes = filter === 'all' 
    ? quotes 
    : quotes.filter(q => q.status === filter);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const vehicleLabels = {
    carro: 'Carro',
    moto: 'Moto',
    embarcacao: 'Embarcação',
    comercial: 'Veículo Comercial',
    outro: 'Outro'
  };

  const serviceLabels = {
    'bate-chapas': 'Bate Chapas',
    'pintura-completa': 'Pintura Completa',
    'pintura-parcial': 'Pintura Parcial',
    polimento: 'Polimento',
    higienizacao: 'Higienização',
    outro: 'Outro'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Painel Administrativo</h1>
              <p className="text-slate-600">LMP PINTURAS - Gestão de Orçamentos</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={fetchQuotes}
                variant="outline"
                disabled={loading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={loading ? 'animate-spin' : ''} size={18} />
                Atualizar
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <LogOut size={18} />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total</CardDescription>
              <CardTitle className="text-3xl">{stats.total}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-slate-600">
                <FileText size={18} />
                <span className="text-sm">Orçamentos</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Pendentes</CardDescription>
              <CardTitle className="text-3xl text-yellow-600">{stats.pending}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock size={18} />
                <span className="text-sm">Aguardando</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Contactados</CardDescription>
              <CardTitle className="text-3xl text-blue-600">{stats.contacted}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-slate-600">
                <Phone size={18} />
                <span className="text-sm">Em andamento</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Concluídos</CardDescription>
              <CardTitle className="text-3xl text-green-600">{stats.completed}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle size={18} />
                <span className="text-sm">Finalizados</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4">
          <span className="text-slate-700 font-medium">Filtrar por:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="contacted">Contactados</SelectItem>
              <SelectItem value="completed">Concluídos</SelectItem>
              <SelectItem value="cancelled">Cancelados</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">
            {filteredQuotes.length} orçamento(s)
          </span>
        </div>

        {/* Quotes List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="animate-spin mx-auto mb-4 text-blue-600" size={40} />
            <p className="text-slate-600">Carregando orçamentos...</p>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="mx-auto mb-4 text-slate-400" size={48} />
              <p className="text-slate-600">Nenhum orçamento encontrado</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-xl">{quote.name}</CardTitle>
                        {getStatusBadge(quote.status)}
                      </div>
                      <CardDescription className="flex items-center gap-2">
                        <Calendar size={14} />
                        {formatDate(quote.timestamp)}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Select
                        value={quote.status}
                        onValueChange={(value) => updateStatus(quote.id, value)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pendente</SelectItem>
                          <SelectItem value="contacted">Contactado</SelectItem>
                          <SelectItem value="completed">Concluído</SelectItem>
                          <SelectItem value="cancelled">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => deleteQuote(quote.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 text-slate-700">
                      <Phone size={18} className="text-blue-600" />
                      <div>
                        <p className="text-xs text-slate-500">Telefone</p>
                        <a href={`tel:${quote.phone}`} className="font-medium hover:text-blue-600">
                          {quote.phone}
                        </a>
                      </div>
                    </div>

                    {quote.email && (
                      <div className="flex items-center gap-2 text-slate-700">
                        <User size={18} className="text-blue-600" />
                        <div>
                          <p className="text-xs text-slate-500">Cliente</p>
                          <p className="font-medium">{quote.name}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-slate-700">
                      <Car size={18} className="text-blue-600" />
                      <div>
                        <p className="text-xs text-slate-500">Veículo</p>
                        <p className="font-medium">{vehicleLabels[quote.vehicleType] || quote.vehicleType}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-slate-700">
                      <Wrench size={18} className="text-blue-600" />
                      <div>
                        <p className="text-xs text-slate-500">Serviço</p>
                        <p className="font-medium">{serviceLabels[quote.serviceType] || quote.serviceType}</p>
                      </div>
                    </div>

                    {quote.description && (
                      <div className="md:col-span-2 flex items-start gap-2 text-slate-700">
                        <FileText size={18} className="text-blue-600 mt-1" />
                        <div>
                          <p className="text-xs text-slate-500">Descrição</p>
                          <p className="text-sm">{quote.description}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`https://wa.me/${quote.phone.replace(/\s/g, '')}`, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <Phone size={16} />
                      WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
